import {
  INotificationContent,
  useNotificationGetUnreadCountQuery,
  useNotificationListInfiniteQuery,
  useNotificationUpdateAllReadMutation,
} from 'api/notification';
import { Header } from 'antd/es/layout/layout';
import { ADMIN_ROUTES } from 'constants/routes';
import React, { useCallback, useMemo } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { GlobalSearch } from 'components/common/GlobalSearch';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { NotificationCard } from 'feature/dashboardTabs/notification/notificationCard';
import { NotificationsEmptyIcon } from 'components/common/icons/NotificationsEmptyIcon';
import { AdminSideBar, Badge, Button, Col, Dropdown, Menu, NotificationToaster, Row, Typography } from 'components';
import styles from './styles.module.scss';
import './dropdown.scss';

const { Text } = Typography;

export const mergeNotificationPages = (pages: INotificationContent[]): Record<string, any>[] => {
  const uniqueNotifications = new Map<string, Record<string, any>>();

  pages.forEach((page) => {
    const notifications = page.content?.[0]?.notifications ?? [];

    notifications.forEach((notification: Record<string, any>) => {
      if (!uniqueNotifications.has(notification.id)) {
        uniqueNotifications.set(notification.id, notification);
      }
    });
  });

  return Array.from(uniqueNotifications.values());
};

export const Admin = React.memo(() => {
  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const navigate = useNavigate();
  const { handleError } = useNotificationMessages();
  const [updateNotification] = useNotificationUpdateAllReadMutation();
  const { data: unreadCount } = useNotificationGetUnreadCountQuery(userId);
  const { data } = useNotificationListInfiniteQuery(
    {
      size: 20,
      userId,
    },
    {
      skip: !userId,
    }
  );
  const notifications = useMemo(() => {
    return mergeNotificationPages(data?.pages || []) || [];
  }, [data]);

  const onRedirectToAllNotifications = useCallback(
    () => navigate(`${ADMIN_ROUTES.dashboardPage}/notification`),
    [navigate]
  );

  const handleAllReadChange = useCallback(async () => {
    try {
      const { error } = await updateNotification(userId);
      if (error) {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [handleError, updateNotification, userId]);

  const menu = (
    <Menu
      items={[
        {
          className: 'notification-dropdown-menu',
          key: 'select_all',
          label: (
            <Row align={'middle'} justify={'space-between'} className={styles.column_item}>
              <Col>
                <Text className={'font-size-16 font-weight-600'}>Notifications</Text>
              </Col>
              <Col>
                <Button
                  size={'small'}
                  type={'text'}
                  variant={'text'}
                  className={'underline-button'}
                  onClick={handleAllReadChange}
                >
                  <Text underline style={{ color: '#05796B' }}>
                    Mark All as Read
                  </Text>
                </Button>
              </Col>
            </Row>
          ),
        },
        ...notifications.map((column: any) => ({
          key: column.id,
          label: (
            <Col style={{ width: '390px' }}>
              <NotificationCard key={column.id} data={column} />
            </Col>
          ),
        })),
        {
          className: 'column-dropdown-menu-footer',
          key: 'all_notifications',
          label: (
            <Row justify="center">
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                className={'underline-button'}
                onClick={onRedirectToAllNotifications}
              >
                <Text underline style={{ color: '#05796B' }}>
                  All Notifications
                </Text>
              </Button>
            </Row>
          ),
        },
      ]}
    />
  );
  return (
    <Row justify={'start'} wrap={false} className={styles.home}>
      <Col>
        <Col className={styles.side_bar}>
          <AdminSideBar />
        </Col>
      </Col>
      <Col></Col>
      <Col className={styles.content_bar}>
        <Header className={styles.admin_header}>
          <Row align={'middle'} justify={'space-between'}>
            <Col>
              <GlobalSearch />
            </Col>
            <Col>
              <NotificationToaster userId={userId} />
              <Dropdown
                dropdownRender={() => <div style={{ width: 420 }}>{menu}</div>}
                trigger={['click']}
                placement="bottomLeft"
              >
                <Badge count={unreadCount?.data} size={'small'} overflowCount={99}>
                  <Button size={'small'} type={'text'} variant={'text'} icon={<NotificationsEmptyIcon />} />
                </Badge>
              </Dropdown>
            </Col>
          </Row>
        </Header>
        <Outlet />
      </Col>
    </Row>
  );
});
